// src/pages/msme/MSMEProcessScoreHome.jsx
import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faDownload,
  faChartLine,
  faChevronDown,
  faChevronUp,
  faCubes,
  faUserCircle,
  faFilePdf,
  faBuilding,
  faVrCardboard,
  faFlag,
  faUserShield,
  faUsers,
  faIndustry,
  faHandshake,
  faGavel,
  faSlidersH,
} from "@fortawesome/free-solid-svg-icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend);

const DUMMY_MSMES = [
  {
    id: "MSME-1001",
    name: "Amrit Snacks Pvt Ltd",
    vertical: "FMCG",
    location: "Kanpur, UP",
    owner: "A. Kumar",
    bank: "SBI",
    flagged: false,
    selfScore: 72,
    auditedScore: 65,
    digitalTwin: true,
  },
  {
    id: "MSME-1002",
    name: "Sona Garments",
    vertical: "Textile",
    location: "Surat, GJ",
    owner: "S. Joshi",
    bank: "HDFC",
    flagged: false,
    selfScore: 82,
    auditedScore: 80,
    digitalTwin: false,
  },
  {
    id: "MSME-1003",
    name: "R.K. Foundry",
    vertical: "Manufacturing",
    location: "Jamshedpur, JH",
    owner: "R. Kumar",
    bank: "ICICI",
    flagged: true,
    selfScore: 58,
    auditedScore: 60,
    digitalTwin: true,
  },
  {
    id: "MSME-1004",
    name: "FreshFarm Foods",
    vertical: "Agro Processing",
    location: "Nashik, MH",
    owner: "L. Mathew",
    bank: "SBI",
    flagged: false,
    selfScore: 91,
    auditedScore: 88,
    digitalTwin: false,
  },
];

const ScoreRing = ({ value, label }) => {
  const circumference = 2 * Math.PI * 16;
  const offset = circumference - (value / 100) * circumference;
  return (
    <div className="relative w-14 h-14">
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="20"
          cy="20"
          r="16"
          stroke="gray"
          strokeWidth="4"
          fill="transparent"
        />
        <circle
          cx="20"
          cy="20"
          r="16"
          stroke="#10B981"
          strokeWidth="4"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-xs">
        <span className="font-bold text-green-400">{value}</span>
        <span className="text-[10px] text-gray-400">{label}</span>
      </div>
    </div>
  );
};

const roleIcons = {
  Owner: faUserCircle,
  Bank: faHandshake,
  ARC: faGavel,
  Government: faUsers,
  Advisor: faIndustry,
  Auditor: faUserShield,
};

// ✅ Generate Mock Chart Data
const generateMockChartData = (role, months, scoreType) => {
  const allMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const selectedMonths = allMonths.slice(-months);

  const base = {
    Owner: 70,
    Bank: 75,
    ARC: 60,
    Government: 80,
    Advisor: 78,
    Auditor: 65,
  }[role];

  const self = selectedMonths.map((_, i) =>
    Math.round(base + Math.sin(i) * 5 + Math.random() * 3)
  );
  const audit = selectedMonths.map((_, i) =>
    Math.round(base - 5 + Math.cos(i) * 4 + Math.random() * 3)
  );

  const datasets = [];
  if (scoreType === "Self" || scoreType === "Both") {
    datasets.push({
      label: "Self Score",
      data: self,
      borderColor: "#10B981",
      backgroundColor: "rgba(16,185,129,0.2)",
      tension: 0.4,
      fill: true,
    });
  }
  if (scoreType === "Audited" || scoreType === "Both") {
    datasets.push({
      label: "Audited Score",
      data: audit,
      borderColor: "#FACC15",
      backgroundColor: "rgba(250,204,21,0.2)",
      tension: 0.4,
      fill: true,
    });
  }

  return {
    labels: selectedMonths,
    datasets,
  };
};

const MSMEProcessScoreHome = () => {
  const [expandedId, setExpandedId] = useState(null);
  const [role, setRole] = useState("Government");
  const [query, setQuery] = useState("");
  const [months, setMonths] = useState(6);
  const [scoreType, setScoreType] = useState("Both");

  const filteredByRole = useMemo(() => {
    switch (role) {
      case "Owner":
        return DUMMY_MSMES.slice(0, 1);
      case "Bank":
        return DUMMY_MSMES.filter((m) => m.bank === "SBI");
      case "ARC":
        return DUMMY_MSMES.filter((m) => m.flagged);
      case "Advisor":
        return DUMMY_MSMES.slice(1, 3);
      case "Auditor":
        return DUMMY_MSMES;
      default:
        return DUMMY_MSMES;
    }
  }, [role]);

  const filtered = filteredByRole.filter((m) =>
    m.name.toLowerCase().includes(query.toLowerCase())
  );

  const chartData = useMemo(
    () => generateMockChartData(role, months, scoreType),
    [role, months, scoreType]
  );

  return (
    <div className="min-h-screen bg-gray-950 text-gray-200 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-green-400 flex items-center gap-2">
          <FontAwesomeIcon icon={faChartLine} />
          MSME Process Score Dashboard
        </h1>
        {/* Role Selector */}
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={roleIcons[role]} className="text-green-400" />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="bg-gray-800 text-gray-200 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-green-600"
          >
            <option>Owner</option>
            <option>Bank</option>
            <option>ARC</option>
            <option>Government</option>
            <option>Advisor</option>
            <option>Auditor</option>
          </select>
        </div>
      </div>

      {/* Filters + Chart */}
      <div className="bg-gray-900 p-4 rounded-2xl shadow mb-5">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-green-300 font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={faChartLine} /> Time-based Score Trend
          </h2>
          <FontAwesomeIcon icon={faSlidersH} className="text-gray-500" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-3">
          {/* Month range filter */}
          <div className="flex items-center gap-1">
            {[3, 6, 12].map((m) => (
              <button
                key={m}
                onClick={() => setMonths(m)}
                className={`px-3 py-1 text-xs rounded ${
                  months === m
                    ? "bg-green-700 text-white"
                    : "bg-gray-800 hover:bg-gray-700"
                }`}
              >
                {m}M
              </button>
            ))}
          </div>

          {/* Score type filter */}
          <div className="flex items-center gap-1">
            {["Self", "Audited", "Both"].map((t) => (
              <button
                key={t}
                onClick={() => setScoreType(t)}
                className={`px-3 py-1 text-xs rounded ${
                  scoreType === t
                    ? "bg-yellow-700 text-white"
                    : "bg-gray-800 hover:bg-gray-700"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="h-56">
          <Line
            data={chartData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  labels: { color: "#d1d5db" },
                },
              },
              scales: {
                x: {
                  ticks: { color: "#9ca3af" },
                  grid: { color: "rgba(75,75,75,0.3)" },
                },
                y: {
                  ticks: { color: "#9ca3af" },
                  grid: { color: "rgba(75,75,75,0.3)" },
                },
              },
            }}
          />
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute left-3 top-2.5 text-gray-400"
        />
        <input
          type="text"
          placeholder="Search MSME..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10 pr-3 py-2 w-full rounded bg-gray-800 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-600"
        />
      </div>

      {/* MSME Cards */}
      <div className="grid gap-4">
        {filtered.map((msme) => (
          <div
            key={msme.id}
            className={`rounded-2xl bg-gray-900 shadow-lg p-4 border ${
              msme.flagged ? "border-red-600" : "border-gray-700"
            }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-semibold text-green-300 flex items-center gap-2">
                  <FontAwesomeIcon icon={faBuilding} />
                  {msme.name}
                </h2>
                <p className="text-sm text-gray-400">
                  {msme.vertical} · {msme.location}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Owner: {msme.owner}
                </p>
              </div>

              <div className="flex gap-3">
                <ScoreRing value={msme.selfScore} label="Self" />
                <ScoreRing value={msme.auditedScore} label="Audit" />
              </div>
            </div>

            <div className="flex justify-between items-center mt-3">
              <div className="flex gap-2 flex-wrap">
                {msme.digitalTwin && (
                  <button
                    onClick={() => alert(`🛰️ Launching Digital Twin for ${msme.id}`)}
                    className="px-3 py-1 text-xs bg-blue-700 rounded hover:bg-blue-600 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faVrCardboard} /> Digital Twin
                  </button>
                )}
                <button
                  onClick={() => alert(`📄 Exporting report for ${msme.id}`)}
                  className="px-3 py-1 text-xs bg-gray-700 rounded hover:bg-gray-600 flex items-center gap-1"
                >
                  <FontAwesomeIcon icon={faFilePdf} /> PDF
                </button>
              </div>

              <button
                onClick={() =>
                  setExpandedId(expandedId === msme.id ? null : msme.id)
                }
                className="text-xs text-green-400 flex items-center gap-1 hover:text-green-300"
              >
                {expandedId === msme.id ? (
                  <>
                    Hide Details <FontAwesomeIcon icon={faChevronUp} />
                  </>
                ) : (
                  <>
                    View Details <FontAwesomeIcon icon={faChevronDown} />
                  </>
                )}
              </button>
            </div>

            {expandedId === msme.id && (
              <div className="mt-3 border-t border-gray-700 pt-3 text-sm">
                <p className="text-gray-300 font-semibold mb-2">
                  Process Insights (Mock Data)
                </p>
                <ul className="text-xs text-gray-400 space-y-1">
                  <li>Procurement: Self 70 / Audit 65</li>
                  <li>Production: Self 75 / Audit 68</li>
                  <li>Quality Control: Self 80 / Audit 70</li>
                </ul>
                {msme.flagged && (
                  <div className="mt-3 flex items-center gap-2 text-red-500">
                    <FontAwesomeIcon icon={faFlag} />
                    <span>This MSME is under observation.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="text-center text-xs text-gray-500 mt-6">
        Viewing as:{" "}
        <span className="text-green-400 font-semibold">{role}</span> · © 2025
        SAKSHAM Think5Whys Platform
      </div>
    </div>
  );
};

export default MSMEProcessScoreHome;
